import styled from 'styled-components';

import { PRO_GENDER_CHOICES } from '@wecasa/common/src/constants/pro-accounts';
import type { ProGender, Promotion, Universe, User, WellnessBasketPrices } from '@wecasa/common/src/types/commons';
import { formatDuration, formatPrice } from '@wecasa/common/src/utils/formatting';
import { toRem } from '@wecasa/common/src/utils/styles';
import { getTitleForCategory, isMassage } from '@wecasa/common/src/utils/universes';

import { getCategoryTitleWithCount, getTotalPrestationsCount } from '@wecasa/customer/src/utils/basket';

import { translate } from '@wecasa/i18n';

import Accordion from '@wecasa/web-ui/src/components/accordion/accordion';
import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Separator from '@wecasa/web-ui/src/components/separator/separator';
import WithSeparators from '@wecasa/web-ui/src/components/separator/with-separators';

import useBasketSection from '../../../../../hooks/use-basket-section';
import { CATEGORIES_PATH, PRESTATIONS_PATH, route } from '../../../../../steps/steps';
import PromoCode from '../../../../promo-code/promo-code';
import ReductionLine from '../../../common/sections/prestations/reduction-line';
import ServiceFeeIncludedText from '../../../common/sections/prestations/service-fee-included-text';
import Surcharges from '../../../common/sections/prestations/surcharges';
import Category from './category';

const t = translate('basket');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${toRem(5)} 0 ${toRem(20)};
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 20px;
`;

const TotalLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${toRem(20)};
`;

const ReductionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  row-gap: ${toRem(5)};
  margin: ${toRem(10)} 0;
`;

type PrestationsPricesProps = {
  universe: Universe;
  prices: WellnessBasketPrices;
  proGender?: ProGender | null;
  promotion?: Promotion;
  basketContentDuration: number;
  hasMembership: boolean;
  user?: User;
  shouldShowPromoCode: boolean;
};

const PrestationsPrices = ({
  universe,
  prices,
  promotion,
  proGender,
  basketContentDuration,
  hasMembership,
  user,
  shouldShowPromoCode,
}: PrestationsPricesProps) => {
  // These prices are always shown as standard prices (even with membership active/requested)
  const {
    basketContentWithPrices,
    appointmentSurcharge,
    appointmentSurchargeReason,
    minPriceSurcharge,
    comboPriceReduction,
  } = prices;

  // These prices are dynamic (depending if a membership is requested/active or not)
  const { totalPrice, giftValue, promoValue } = hasMembership ? prices.membership : prices;

  // These prices will only be greater 0 if a membership is requested/active
  const { reduction: membershipReduction } = prices.membership;

  const { isOpen } = useBasketSection({
    data: basketContentWithPrices.length > 0,
    sectionLocations: [route(CATEGORIES_PATH), route(PRESTATIONS_PATH)],
  });

  const hasPromo = !!promoValue;
  const hasGift = !!giftValue;
  // Always show the total price for gift cards and promo codes, even if there is a total of 0
  const hasTotalPrice = !!totalPrice || hasGift || hasPromo;
  const totalPriceFormatted = formatPrice(totalPrice);
  const isMassageWithGenderChoice = isMassage(universe) && !!proGender && proGender !== PRO_GENDER_CHOICES.DONT_CARE;
  const accordionTitle = t(`.services.title${isMassageWithGenderChoice ? `_massage_${proGender}` : ''}`, {
    count: getTotalPrestationsCount(basketContentWithPrices),
  });

  const shouldShowMembershipReduction = hasMembership && hasTotalPrice;
  const hasAnyReduction = shouldShowMembershipReduction || hasGift || hasPromo || comboPriceReduction > 0;

  const subtitle = basketContentDuration ? <Text.SM>{formatDuration(basketContentDuration)}</Text.SM> : undefined;

  return (
    <Container>
      <Separator />
      <StyledAccordion
        icon={hasGift ? 'gift' : 'basket'}
        iconLabel={t(`.services.icon_label.${hasGift ? 'gift' : 'basket'}`)}
        title={accordionTitle}
        subtitle={subtitle}
        titlePreview={hasTotalPrice ? totalPriceFormatted : ''}
        isOpen={isOpen}
      >
        <WithSeparators>
          {basketContentWithPrices.map((basketContentItem, index) => {
            const title = getCategoryTitleWithCount(
              index,
              getTitleForCategory(basketContentItem.reference, universe),
              basketContentWithPrices,
            );

            return <Category key={title} basketContentItem={basketContentItem} universe={universe} title={title} />;
          })}
          <Surcharges
            appointmentSurcharge={appointmentSurcharge}
            appointmentSurchargeReason={appointmentSurchargeReason}
            minPriceSurcharge={minPriceSurcharge}
            universeReference={universe.reference}
          />
          {hasAnyReduction && (
            <ReductionsContainer>
              {comboPriceReduction > 0 && <ReductionLine title={t('.reductions')} value={comboPriceReduction} />}
              {shouldShowMembershipReduction && (
                <ReductionLine
                  title={t('.membership.reduction_wellness')}
                  value={membershipReduction}
                  label={t('.membership.label_full')}
                />
              )}
              {hasPromo && <ReductionLine title={t('.promo_code')} value={promoValue} />}
              {hasGift && <ReductionLine title={t('.gift_card')} value={giftValue} />}
            </ReductionsContainer>
          )}
          {hasTotalPrice && (
            <>
              <TotalLine aria-label={t('.total')}>
                <Text.MDBold>{t('.total')}</Text.MDBold>
                <Text.MDBold>{totalPriceFormatted}</Text.MDBold>
              </TotalLine>
              <ServiceFeeIncludedText />
            </>
          )}
        </WithSeparators>
      </StyledAccordion>
      {hasTotalPrice && shouldShowPromoCode && (
        <PromoCode
          universeReference={universe.reference}
          promotion={promotion}
          hasGift={hasGift}
          totalPrice={totalPrice}
          user={user}
        />
      )}
    </Container>
  );
};

export default PrestationsPrices;
